import { useEffect, useRef, useState } from 'react';
import TokenService from '../../services/Token';
import { useCreditProposalResult } from '../../contexts/CreditProposalResultContext';
import { EnvType } from '../Tpv';
import CreditProposalService from '../../services/CreditProposal';
import { ROUTES_PATH } from '../../constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { CreditProposalFlowStep } from '../../interfaces/CreditProposalResult';
import { useMerchant } from '../../contexts/MerchantContext';
import { SignatureReact } from '@pepper/signature-react';
import PostMessageService from '../../services/PostMessage';
import InsightService from '../../services/Insight';
import AnalyticsService from '../../services/Analytics';
import dayjs from 'dayjs';
import AnalyticsHelper from '../../utils/analyticsHelper';
import { useLoanSimulation } from '../../contexts/LoanSimulationContext';
import { useProduct } from '../../contexts/ProductContext';
import FlowEndService from '../../services/FlowEndService';
import { colors } from '../../styles/Colors';

interface SignatureProps {
  loadingEvent: (data: boolean) => void;
  paymentThirdParty?: string;
  closingEvent: (data: boolean) => void;
  signatureError: (data: string) => void;
}

interface LocationState {
  decisionStatus?: {
    signatureReady?: boolean;
    isDecisionPending?: boolean;
  };
}

export const Signature: React.FC<SignatureProps> = ({
  loadingEvent,
  paymentThirdParty,
  closingEvent,
  signatureError,
}) => {
  const navigate = useNavigate();
  const { creditProposalResult } = useCreditProposalResult();
  const { isNemuru, isApp, isPostDataMessages } = useMerchant();
  const { loanSimulation } = useLoanSimulation();
  const { product } = useProduct();
  const [successEnd, setSuccessEnd] = useState(false);
  const [signatureReady, setSignatureReady] = useState(true);

  const cexToken = TokenService.getToken();
  const location = useLocation();
  const decisionPending = (location.state as LocationState)?.decisionStatus
    ?.isDecisionPending;
  const signature: any = (location.state as LocationState)?.decisionStatus
    ?.signatureReady;
  const environment: EnvType = {
    type: import.meta.env.VITE_ENV,
  };

  const startTime = dayjs();
  let loanTimeOut: NodeJS.Timeout | null = null;
  // session storage almacenado desde la librería de firma
  // en caso de existir una firma para el proceso se almacena en sessionstorage
  const createdSignatureData = sessionStorage.getItem('p_signature');

  const handleSuccess = () => {
    loadingEvent(true);
    if (!(location.state as LocationState)?.decisionStatus) {
      getProposalStep();
    } else {
      loanTimeOut = setTimeout(() => {
        verifyApiCall(0, decisionPending);
      }, 3000);
    }
  };

  const handleError = (error: any[] | any) => {
    // Cada tercero entrega el error con una forma distinta:
    // - Pepper: string con TODOS los códigos del backend unidos por comas ("ESG0014,ESG0012").
    // - Galeon: enum propio ('error' = desconocido, 'cancelled' con doble L).
    // - Rubricae: 'failed' | 'max_attempts' | '' | undefined.
    // Normalizamos a lista de códigos y comprobamos pertenencia en lugar de usar ===.
    const codes = String(error ?? '')
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);
    const has = (...wanted: string[]) =>
      wanted.some((code) => codes.includes(code));

    if (has('expired', 'access_token_expired', 'unknown', 'error', 'max_attempts')) {
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    } else if (has('cancelled', 'canceled')) {
      // Galeon emite 'cancelled' (doble L) y cae al default de la lib padre,
      // por lo que la cancelación llega aquí en vez de a la prop cancel().
      handleCancel();
    } else if (has('DRF0046')) {
      navigate(ROUTES_PATH.END);
    } else if (has('ESG0017', 'ESG0015')) {
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    } else if (has('ESG0014', 'ESG0012')) {
      signatureError('ESG0014');
      if (isPostDataMessages) {
        PostMessageService.InvalidOTP(creditProposalResult?.credit_proposal_id);
      }
    } else if (codes.length && !has('signed')) {
      // Resto de errores (códigos de Pepper como ESG0015/ESG0009/..., 'failed' de
      // Galeon/Rubricae, etc.): se muestran en el banner igual que el resto de
      // pasarelas. error-helper.ts mapea el texto y los no mapeados caen en el
      // mensaje genérico. Se excluye 'signed' (Galeon), que no es un error.
      signatureError(codes[0]);
    }
  };

  const handleCancel = () => {
    navigate(ROUTES_PATH.UNKNOWN_ERROR);
  };

  const getProposalStep = async (isRetry?: boolean) => {
    loadingEvent(true);
    try {
      if (creditProposalResult) {
        const creditProposalResponse =
          await CreditProposalService.getCreditProposal(
            creditProposalResult?.credit_proposal_id,
          );
        if (
          creditProposalResponse.flow_step ===
          CreditProposalFlowStep.VERIFICATION
        ) {
          loanTimeOut = setTimeout(() => {
            verifyApiCall(0, true);
          }, 3000);
        } else if (
          creditProposalResponse.flow_step === CreditProposalFlowStep.ACTIVATION
        ) {
          loanTimeOut = setTimeout(() => {
            verifyApiCall(0, false);
          }, 3000);
        } else {
          if (isRetry) {
            loadingEvent(false);
            navigate(ROUTES_PATH.UNKNOWN_ERROR);
          } else {
            setTimeout(() => {
              getProposalStep(true);
            }, 1000);
          }
        }
      } else {
        loadingEvent(false);
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    } catch (error) {
      loadingEvent(false);
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }
  };

  const verifyApiCall = async (counter: number, isVerify: boolean) => {
    const count = counter;
    let response;
    try {
      loadingEvent(true);
      if (isVerify) {
        !isNemuru &&
          (response = await CreditProposalService.verifyBankreader(
            creditProposalResult!.credit_proposal_id,
          ));
        AnalyticsService.verify();
        setSuccessEnd(false);
        loadingEvent(false);
        navigate(ROUTES_PATH.END);
      } else {
        !isNemuru &&
          (response = await CreditProposalService.activateCreditProposal(
            creditProposalResult!.credit_proposal_id,
          ));
        AnalyticsService.purchase({
          currency: 'EUR',
          value: loanSimulation?.first_fee,
          items: [
            {
              item_id: product?.code || creditProposalResult.product,
              item_name: product?.name || creditProposalResult.product,
            },
          ],
          transaction_id: creditProposalResult.credit_proposal_id,
        });
        AnalyticsService.generateLead({
          currency: 'EUR',
          value: loanSimulation?.first_fee,
        });
        setSuccessEnd(false);
        navigate(ROUTES_PATH.END);
      }
      InsightService.logTrace('CreditProposal - Create Loan', [
        { creditProposal: response },
      ]);
    } catch (error: any) {
      const errorCode = error.response?.data.result.errors[0]?.code;
      if (
        error.result?.http_code === 422 &&
        errorCode === 'DRF0027' &&
        count < 3
      ) {
        loadingEvent(true);
        loanTimeOut = setTimeout(() => {
          verifyApiCall(count + 1, isVerify);
        }, 3000);
      } else {
        loadingEvent(false);
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
      InsightService.logTrace('CreditProposal - Create Loan ERROR', [
        { creditProposalError: error },
      ]);
    } finally {
      if (counter === 0) loadingEvent(false);
    }
  };

  const handleContractApp = (data: string) => {
    PostMessageService.SignatureContract(data);
  };

  const handleSignatureStep = (step: string, thirdParty?: string) => {
    const thirdPartyMap: Record<string, string | undefined> = {
      PEP: 'PEPPER',
      RUB: 'RUBRICAE',
      CER: 'ECERTIC',
      GAL: 'GALEON',
    };
    const third = thirdParty ? thirdPartyMap[thirdParty] : undefined;
    switch (step) {
      case 'SHOW_CONTRACT':
        AnalyticsService.show_contract();
        break;
      case 'RESEND_OTP':
        AnalyticsService.resendOtp({
          time_elapsed: AnalyticsHelper.getTimeElapse({
            initTime: startTime,
            finishTime: dayjs(),
          }),
        });
        break;
      case 'PIN_ERROR':
        AnalyticsService.pinError();
        break;
      case 'CONTINUE_SIGNATURE':
        AnalyticsService.continueSignature({
          time_elapsed: AnalyticsHelper.getTimeElapse({
            initTime: startTime,
            finishTime: dayjs(),
          }),
          step_number: 6,
          third,
        });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (location.state) {
      setSignatureReady(signature);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    if (FlowEndService.getPepperOneEnded() === 'END') {
      loadingEvent(true);
      setTimeout(() => {
        navigate(ROUTES_PATH.END);
      }, 1000);
    } else if (cexToken && environment && !successEnd && signatureReady) {
      loadingEvent(false);
    }
    return () => clearTimeout(loanTimeOut);
  }, []);

  return (
    <>
      {cexToken && environment && !successEnd && signatureReady && (
        <SignatureReact
          creditProposalID={creditProposalResult?.credit_proposal_id!}
          currentEnvironment={environment}
          token={cexToken}
          // generalPadding="20px 2.5rem"
          successEnd={handleSuccess}
          errorMessage={handleError}
          cancel={handleCancel}
          isApp={isApp}
          sendContractData={handleContractApp}
          stepEvent={(step: string, thirdParty?: string) =>
            handleSignatureStep(step, thirdParty)
          }
          signatureData={JSON.parse(createdSignatureData)}
          letterColor={colors.gray6}
        />
      )}
    </>
  );
};
